import type { WireAgent, WireAssignment, WireAuditRow, WireConnection, WireDepartment, WireDepartmentDetail, WireReadiness, WireTool } from '@/api/wire';
import { toolName, toolPurpose } from '@/app/technical';
import type { ItemContext, Party } from '@/ui/context';
import { LEVEL_LABEL, LEVEL_SHORT, type Level } from '@/ui/meter';
import { twinLabel } from '@/ui/people';
import { dayWords, humanise, scopeWords, stateWords } from '@/ui/words';
import { nameFromAddress, type Who } from '@/ui/who';
import { actionWords, isCatalogued, ruleOfRow, ruleWords, outcomeWords, targetWords } from './auditWords';

/**
 * What each oversight record says about itself (ADR-0155): builders that
 * turn a row a screen already holds into an `ItemContext`. Pure: no hooks,
 * no fetches, so each can be read, tested and argued with. They say only
 * what the row carries; what it does not carry is left out or said to be
 * unrecorded, never guessed. Nothing here names an id, a code or an
 * address: the superadmin's technical detail stays on the screens' own
 * `<Technical>` lines, and a person is named, never addressed.
 */

const DAY = 86_400_000;
/** A decision that has waited this many days is said to be late. */
export const LATE_AFTER_DAYS = 3;

/** An address read aloud as a name: nothing built here shows one. */
export const clean = (s: string): string => s.replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, (a) => nameFromAddress(a));

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** "a, b and 2 more": a list that stays one line long. */
export function listWords(items: readonly string[], max = 4): string {
  if (items.length === 0) return '';
  if (items.length <= max) return items.length === 1 ? items[0]! : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]!}`;
  return `${items.slice(0, max).join(', ')} and ${items.length - max} more`;
}

/** How long ago, as a person says it. */
export function waitedWords(iso: string, now: number): string {
  const ms = Math.max(0, now - Date.parse(iso));
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return 'less than an hour';
  if (hours < 48) return plural(hours, 'hour');
  return plural(Math.floor(ms / DAY), 'day');
}

const daysSince = (iso: string, now: number): number => Math.floor(Math.max(0, now - Date.parse(iso)) / DAY);

/** Another page's address, kept only when it is a plain path the task endpoint accepts. */
const plainRoute = (href: string): string | undefined => {
  const path = href.split(/[?#]/)[0] ?? '';
  return /^\/[A-Za-z0-9/_.-]{0,200}$/.test(path) ? path : undefined;
};

const asParty = (who: Who, id: string | null | undefined, role?: string): Party | null => {
  const p = who.actor(id);
  return p ? { ...p, ...(role ? { role } : {}) } : null;
};

const known = (parties: Array<Party | null>): Party[] => parties.filter((p): p is Party => p !== null);

// ---------------------------------------------------------------------------
// What waits on a decision: the approvals queue and the Workspace's Now and
// Proposals tabs share one table of kinds, because both are lists of "a
// thing a person decides", differing in who the person is.
// ---------------------------------------------------------------------------

export interface QueueItemLike {
  kind: string;
  group?: string | undefined;
  id: string;
  title: string;
  department: string | null;
  askedBy: string;
  waitingSince: string;
  href: string;
}

interface KindSpec {
  noun: string;
  what: string;
  /** Where it came from, given the name of whoever raised it. */
  source: (by: string | null) => string;
  need: string;
  tip: string;
  /** Said first when it has waited past the limit. */
  late?: string;
  instead: string;
  action: string;
  /** What spends a person's code, when something here does. */
  code?: string;
  /** The software factory's agents are on it. */
  factory?: boolean;
  /** Nothing to decide: something to watch or to put right. */
  watch?: boolean;
}

const prepared = (by: string | null, what: string) => (by ? `${what}, raised by ${by}` : what);

const KINDS: Record<string, KindSpec> = {
  draft: {
    noun: 'draft',
    what: 'A message a worker prepared and left as a draft. Nothing has been sent.',
    source: (by) => (by ? `A draft prepared by ${by}` : 'A draft prepared by a worker'),
    need: 'Read it, then send it as it is, change it first, or discard it. Only a person sends; the platform never does.',
    tip: 'Open the draft, read it once, and send, edit or discard it.',
    late: 'A message that waits this long may have been overtaken: if it no longer fits, discard it rather than send it late.',
    instead: 'If you do nothing it stays a draft and nothing goes out.',
    action: 'Read the draft',
  },
  request: {
    noun: 'request',
    what: 'A request another department made of this one, routed to its lead because no agent could take it.',
    source: (by) => prepared(by, 'A request from another department'),
    need: 'Answer it, or say no with a reason. Asking confers nothing the department does not already hold.',
    tip: 'Open the request and either do what it asks or decline it with a reason, so the asker is not left guessing.',
    late: 'The asker is still waiting; a short no is kinder than silence.',
    instead: 'Nothing happens by itself: the request stays here until someone answers.',
    action: 'Open the request',
  },
  stranger: {
    noun: 'held sender',
    what: 'Someone the platform does not know wrote in, and their message is held until a person decides.',
    source: (by) => prepared(by, 'A message from a sender nobody has approved'),
    need: 'Decide whether they are someone we deal with. Nothing they sent is acted on while they are held.',
    tip: 'Open it, read what they sent, and approve them only if they are a real counterpart.',
    instead: 'Leaving them held is safe: nothing is read into the platform from them, and nothing is sent back.',
    action: 'Open the held message',
  },
  promotion: {
    noun: 'promotion',
    what: 'A change to a department’s fixed records (a contact or a company) that an agent or a person proposed.',
    source: (by) => prepared(by, 'A promotion proposed'),
    need: 'Approve it or refuse it, with a reason. The fixed records change only on a person’s decision.',
    tip: 'Open it, compare what it would change with what is there, and approve or refuse with a reason.',
    instead: 'Refusing keeps the record exactly as it is; doing nothing does the same.',
    action: 'Review the promotion',
  },
  build: {
    noun: 'change',
    what: 'A change built from a spec its person confirmed. It passed its tests and now waits for a lead or a superadmin.',
    source: (by) => prepared(by, 'A change the software factory built and tested'),
    need: 'The second of two human steps: approve it to ship, or send it back with a reason. The first, the read-back, was already confirmed by whoever asked.',
    tip: 'Open the change, read its read-back and what the sandbox preview did to the test rows, and approve or send it back.',
    late: 'Whoever asked is waiting on it.',
    instead: 'Sending it back returns it to draft with your reason; the third try is an engineer’s. Nothing ships without you.',
    action: 'Review the change',
    factory: true,
  },
  incident: {
    noun: 'incident',
    what: 'Something went wrong, and no person has acknowledged it yet.',
    source: (by) => prepared(by, 'Raised when a rule fired or something failed'),
    need: 'Read what happened, and acknowledge it with a note so the next person knows it was seen.',
    tip: 'Open the incident, read what happened and what was already done, and acknowledge it with a note.',
    late: 'It has gone unacknowledged long enough that someone should look today.',
    instead: 'Acknowledging does not undo anything; if a rule paused something, starting it again is a separate decision.',
    action: 'Open the incident',
  },
  ledger: {
    noun: 'ledger edit',
    what: 'A file of the ledger, the platform’s source of truth, was edited in Drive and waits for a person to look.',
    source: (by) => prepared(by, 'A ledger file edited in Drive'),
    need: 'See what changed in the file and decide whether it stands.',
    tip: 'Open it, read the change line by line, and decide.',
    instead: 'Until you decide, the platform keeps working from the ledger as it was committed.',
    action: 'Review the edit',
  },
  erasure: {
    noun: 'erasure request',
    what: 'A person asked for their data to be erased. Their address is already suppressed, so nothing more is sent to them.',
    source: (by) => prepared(by, 'A person’s request to be forgotten'),
    need: 'Carry it out under a single-use code made for this one request. A conversation shared with others is counted for a person to redact and is never claimed.',
    tip: 'Open it, check what it touches, make the code for this request and carry it out.',
    late: 'People are entitled to a prompt answer on this one.',
    instead: 'Waiting does not restart any mail to them; it only delays the erasure.',
    action: 'Open the request',
    code: 'Carrying it out spends a single-use code bound to this request.',
  },
  lift: {
    noun: 'request to lift a stop',
    what: 'Someone once said they did not want to hear from us, and a colleague is asking to write to them again.',
    source: (by) => prepared(by, 'A colleague’s request to lift a stop'),
    need: 'Decide whether the stop may be lifted. By default it stays.',
    tip: 'Open it and read why it is being asked. Lift it only if the person has since said yes in their own words.',
    instead: 'Declining keeps them suppressed, which is always safe.',
    action: 'Open the request',
    code: 'Lifting spends a single-use code; declining needs none.',
  },
  invitation: {
    noun: 'invitation',
    what: 'Someone was invited to join, and an invitation confers nothing until a person commits their roster line.',
    source: (by) => prepared(by, 'An invitation'),
    need: 'Commit their roster line if you agree, which is what actually adds them.',
    tip: 'Open it, check the line it would add, and commit it to the roster if they should join.',
    instead: 'Until a line is committed they have no access, however long the invitation waits.',
    action: 'Open the invitation',
  },
  campaign: {
    noun: 'campaign',
    what: 'A campaign drafted by an agent, waiting for approval. Every step of it is a person’s draft or task; the platform sends nothing.',
    source: (by) => prepared(by, 'A campaign drafted for approval'),
    need: 'Read the campaign and approve it or send it back.',
    tip: 'Open it, read the first message and who it goes to, and approve or send it back with what to change.',
    instead: 'Not approving it simply keeps it from starting; nothing is lost.',
    action: 'Review the campaign',
    code: 'Approving spends a single-use code.',
  },
  mailing: {
    noun: 'mailing',
    what: 'A mailing drafted for approval. The platform prepares it; a person decides whether it goes.',
    source: (by) => prepared(by, 'A mailing drafted for approval'),
    need: 'Read the mailing and approve it or send it back.',
    tip: 'Open it, read it as a recipient would, and check who it goes to before approving.',
    instead: 'Not approving it keeps it from going; nothing is lost.',
    action: 'Review the mailing',
    code: 'Approving spends a single-use code.',
  },
  sequence: {
    noun: 'series of emails',
    what: 'A series of follow-up emails drafted for approval. Every step is a person’s draft or task.',
    source: (by) => prepared(by, 'A series of emails drafted for approval'),
    need: 'Read the series and approve it or send it back.',
    tip: 'Open it, read each step in order, and approve only if you would send each one yourself.',
    instead: 'Not approving it keeps it from starting; nothing is lost.',
    action: 'Review the series',
  },
  counterparties: {
    noun: 'company',
    what: 'A company that came up in the work and that nobody has approved yet.',
    source: (by) => prepared(by, 'A company named in the work'),
    need: 'Decide whether the company is one we deal with.',
    tip: 'Open it, check who the company is, and approve it only if it is a real counterpart.',
    instead: 'Leaving it unapproved is safe: it is treated as a stranger until someone decides.',
    action: 'Open the company',
    code: 'Approving a company spends a single-use code.',
  },
  task_tool: {
    noun: 'tool a twin’s task is waiting on',
    what: 'A twin’s task cannot go on because it needs a tool it does not hold.',
    source: (by) => prepared(by, 'A twin’s confirmed task, stopped at a tool it lacks'),
    need: 'Lend the tool to that one task for as long as you say, or refuse with a reason that goes back to its person.',
    tip: 'Open the twins’ tasks tab, check the tool fits what the task is for, and grant it for the shortest time that will do, or refuse with a reason.',
    late: 'Its person is waiting on a task that cannot move.',
    instead: 'A refusal hands the task back to its person with your reason; nothing is lost.',
    action: 'Open the twins’ tasks',
    code: 'Granting spends a single-use code bound to that task and tool; refusing needs none.',
  },
  suggestion: {
    noun: 'suggestion',
    what: 'A suggestion for a page of the platform, written up as a spec by Norum Tech. Nothing is built from it.',
    source: (by) => prepared(by, 'A suggestion for the platform'),
    need: 'Decide once: accept it, which opens it as an issue for the engineers, or decline it.',
    tip: 'Read the spec and accept it if you would want it built; the decision is final either way.',
    instead: 'Declining closes it. Doing nothing leaves it here, and nothing is built.',
    action: 'Read the suggestion',
  },
  assignment_ending: {
    noun: 'assignment running out',
    what: 'An agent’s assignment is close to its end. When it ends, what it granted stops on the next call.',
    source: () => 'The platform’s count of live assignments and their end dates',
    need: 'Decide whether the work continues.',
    tip: 'If the work continues, grant the agent a fresh assignment from its page before this one ends. If not, do nothing: it lapses by itself.',
    instead: 'Nothing has to be taken away; the capability simply stops at the end date.',
    action: 'Open the agent',
    watch: true,
  },
  vendor_paused: {
    noun: 'paused vendor',
    what: 'A vendor is paused, so every call to it is refused at the gateway.',
    source: () => 'A superadmin paused it from Plans and usage',
    need: 'Resume it once it is safe to reach again, or leave it paused.',
    tip: 'Open Plans and usage, read why it was paused, and resume it only once that reason is settled.',
    instead: 'Leaving it paused costs nothing; the agents that need it will be refused until it is resumed.',
    action: 'Open Plans and usage',
    code: 'Resuming spends a single-use billing code.',
    watch: true,
  },
  rotation_due: {
    noun: 'key due to be changed',
    what: 'A key the platform holds for an outside service has reached the age at which it should be replaced.',
    source: () => 'The platform’s rotation term for that connection',
    need: 'Put in a new key, then revoke the old one at the vendor.',
    tip: 'Open Connections, put in the new key under a code, then revoke the old one at the vendor’s own site.',
    instead: 'The old key keeps working until it is revoked, so waiting is a risk and not an outage.',
    action: 'Open Connections',
    code: 'Putting in a key spends a single-use code.',
    watch: true,
  },
  vendor_alert: {
    noun: 'vendor near its line',
    what: 'A vendor is near the point where its free allowance ends, or has stopped at it.',
    source: () => 'The platform’s count of use against each vendor’s allowance',
    need: 'Decide whether to raise the line or let it hold.',
    tip: 'Open Plans and usage and look at the pace: raise the line only if the use is wanted, since raising it allows spending.',
    instead: 'Letting it hold is safe: at the line the gateway refuses the call before anything is spent.',
    action: 'Open Plans and usage',
    code: 'Raising a line spends a single-use billing code; lowering one needs none.',
    watch: true,
  },
  first_days: {
    noun: 'new person’s first days',
    what: 'A new person’s first days on the platform are not finished yet.',
    source: () => 'The platform’s list of first-day steps for each new person',
    need: 'See which steps are open and help them, or their lead, finish.',
    tip: 'Open it, see which first-day steps are open, and nudge the person or their lead.',
    instead: 'Nothing breaks if it waits; they simply have less set up.',
    action: 'Open their first days',
    watch: true,
  },
  tool_unadmitted: {
    noun: 'tool not yet admitted',
    what: 'A tool is signed in the ledger but nobody has admitted it, so every call to it is refused.',
    source: () => 'A signed manifest in the ledger with no admission yet',
    need: 'Admit it, or leave it refused.',
    tip: 'Open the tool library and admit it if you want agents to use it. A vendor that processes outside the EU needs its paperwork marked on the Workspace first.',
    instead: 'Leaving it unadmitted is safe: no agent can reach it.',
    action: 'Open the tool library',
    code: 'Admitting spends a single-use code.',
    watch: true,
  },
  department_stopped: {
    noun: 'stopped department',
    what: 'A department’s agents are stopped. Nothing is deleted; they simply cannot act.',
    source: () => 'A superadmin stopped it',
    need: 'Start its agents again once the reason is settled, or leave it stopped.',
    tip: 'Open the department and start its agents again only once the reason it was stopped is settled.',
    instead: 'Other departments are untouched, so leaving it stopped affects only this one.',
    action: 'Open the department',
    code: 'Starting it again spends a single-use code.',
    watch: true,
  },
  agent_stopped: {
    noun: 'stopped agent',
    what: 'An agent is stopped. Its history and everything it built are untouched; it cannot act.',
    source: () => 'A superadmin stopped it',
    need: 'Start it again once the reason is settled, or leave it stopped.',
    tip: 'Open the agent and start it again only once the reason it was stopped is settled.',
    instead: 'Leaving it stopped costs nothing but its work.',
    action: 'Open the agent',
    code: 'Starting it again spends a single-use code.',
    watch: true,
  },
  lead_task: {
    noun: 'proposal an agent put to its lead',
    what: 'An agent noticed something worth doing and put it to its lead as a task. Nothing happens until a person decides.',
    source: (by) => prepared(by, 'A proposal an agent put to its lead'),
    need: 'Decide whether to do it, hand it on, or leave it.',
    tip: 'Open it, read what the agent noticed, and either take it on or close it with a note on why.',
    instead: 'An agent’s proposal waits on a person; it is never carried out by itself.',
    action: 'Open the task',
  },
  change: {
    noun: 'change someone asked for',
    what: 'A change a person asked for, compiled into a spec and waiting for its read-back to be confirmed or for a decision.',
    source: (by) => prepared(by, 'A change asked for in plain words'),
    need: 'Whoever asked confirms the read-back; then the department’s lead or a superadmin approves.',
    tip: 'Open it and read the read-back. If it says what was meant, confirm it; if not, ask for it to be changed.',
    instead: 'Nothing is built until the read-back is confirmed and a lead approves it.',
    action: 'Open the change',
  },
  skill_candidate: {
    noun: 'pattern agents keep noticing',
    what: 'Agents in a department noticed the same pattern again and again across runs.',
    source: () => 'The platform’s count of patterns across runs',
    need: 'Decide whether the pattern is worth writing down as a skill.',
    tip: 'Read the pattern; if it deserves to be a skill, a person writes it and commits the file. An agent never writes one.',
    instead: 'Ignoring it is fine; it only stays counted.',
    action: 'Open it',
  },
};

const FALLBACK: KindSpec = {
  noun: 'item',
  what: 'Something waiting on a person.',
  source: (by) => prepared(by, 'Raised by the platform'),
  need: 'Open it and decide.',
  tip: 'Open it, read what it asks and decide.',
  instead: 'Nothing happens by itself while it waits.',
  action: 'Open it',
};

export function explainQueueItem(item: QueueItemLike, who: Who, act: { open: (item: { href: string; department: string | null }) => void }, now = Date.now()): ItemContext {
  const spec = KINDS[item.kind] ?? FALLBACK;
  const brought = who.actor(item.askedBy);
  const days = daysSince(item.waitingSince, now);
  const waited = waitedWords(item.waitingSince, now);
  const late = days >= LATE_AFTER_DAYS;
  const dept = item.department ? who.label(item.department) : null;
  const lead = who.lead(item.department);
  const proposal = item.group === 'proposal';
  const route = plainRoute(item.href);
  return {
    subject: { kind: spec.noun, title: clean(item.title) },
    summary: `${spec.what}${dept ? ` It belongs to ${dept}.` : ''}`,
    need: spec.code ? `${spec.need} ${spec.code}` : spec.need,
    why: proposal
      ? `It is a proposal: someone put it forward and it does nothing until a person decides. It has waited ${waited}.`
      : spec.watch
        ? `The platform lists it for you because it can only be put right by a superadmin. It has been open ${waited}.`
        : `${lead && lead.name !== 'You' ? `${lead.name} leads ${dept ?? 'it'} and you can decide it too. ` : ''}It waits on a decision from you, and has waited ${waited}.`,
    source: { label: spec.source(brought ? brought.name : null), when: dayWords(item.waitingSince) },
    broughtBy: brought ? { ...brought, when: dayWords(item.waitingSince) } : null,
    owner: lead ?? { ...who.me, role: spec.watch ? 'watches it' : 'decides it' },
    involved: spec.factory ? who.agentsOf('tech') : undefined,
    knows: item.department ? who.knowers(item.department) : undefined,
    facts: [
      ['Waiting for', waited],
      ['Department', dept ?? 'Company-wide'],
    ],
    next: {
      tip: `${late ? `${spec.late ?? `It has waited ${waited}, longer than most things should.`} ` : ''}${spec.tip}`,
      instead: spec.instead,
      actions: [{ label: spec.action, run: () => act.open(item), primary: true }],
    },
    ...(route && !spec.watch ? { task: { title: `Decide: ${clean(item.title)}`.slice(0, 200), from: route } } : {}),
  };
}

// ---------------------------------------------------------------------------
// Agents and what they hold
// ---------------------------------------------------------------------------

function holdingsWords(a: Pick<WireAgent, 'holdings'>): string {
  const scopes = a.holdings.scopes.map(scopeWords);
  const tools = a.holdings.tools.map(toolName);
  return listWords([...scopes, ...tools], 5);
}

export function agentDisplay(a: WireAgent): string {
  return a.agentKind === 'twin' && a.principal ? twinLabel(a.givenName, a.principal.displayName) : `${a.givenName} ${a.familyName}`;
}

/**
 * Whether an agent's Workspace account has been signed in to once, from the
 * Readiness report's own per-account check (ADR-0115, 0162). Undefined when
 * the report is not to hand or does not name the account: the panel then
 * says what it said before, never a guess.
 */
export type AccountStanding = 'active' | 'waiting' | 'unknown';

export function accountStandingOf(readiness: WireReadiness | undefined, email: string | null): AccountStanding | undefined {
  if (!readiness || !email) return undefined;
  const id = `account:${email.toLowerCase()}`;
  for (const f of readiness.features) {
    const check = f.checks.find((c) => c.id === id);
    if (check) return check.state === 'done' ? 'active' : check.state === 'todo' ? 'waiting' : 'unknown';
  }
  return undefined;
}

const ACCOUNT_WORDS: Record<AccountStanding, string> = {
  active: 'Yes, a Workspace account, signed in to and active',
  waiting: 'Yes, a Workspace account nobody has signed in to yet: Gmail refuses what it does until someone does',
  unknown: 'Yes, a Workspace account; Google did not say whether it is active',
};

export function explainAgent(a: WireAgent, who: Who, extra: { assignments?: readonly WireAssignment[] | undefined; account?: AccountStanding | undefined } = {}): ItemContext {
  const name = agentDisplay(a);
  const dept = a.department ? who.label(a.department) : null;
  const paused = a.revokedAt !== null;
  const held = holdingsWords(a);
  const kind =
    a.agentKind === 'twin'
      ? `a digital twin${a.principal ? ` of ${a.principal.displayName}` : ''}. It has no address of its own and prepares drafts and events for its person; it never sends`
      : a.agentKind === 'department'
        ? `a worker of ${dept ?? 'a department'}`
        : a.agentKind === 'platform'
          ? 'one of the platform’s own agents'
          : 'a privileged agent of the platform';
  const status = paused
    ? `It is paused${a.revocationReason ? `: “${clean(a.revocationReason)}”` : ''}.`
    : a.liveAssignments === 0
      ? 'It holds nothing right now, so it can do nothing.'
      : `It is working under ${plural(a.liveAssignments, 'live assignment')} and holds ${held || 'nothing yet'}.`;
  const sponsor = asParty(who, a.sponsor.email, 'sponsors it') ?? { name: a.sponsor.displayName, kind: 'person' as const, role: 'sponsors it' };
  const sponsorParty: Party = sponsor.name === 'You' ? sponsor : { ...sponsor, name: a.sponsor.displayName };
  const endingSoon = (extra.assignments ?? []).filter((x) => x.state === 'ending_soon');
  const revoker = a.revokedBy ? asParty(who, a.revokedBy) : null;
  const selfName = name;
  const others = who.knowers(a.department).filter((p) => p.name !== selfName && p.name !== sponsorParty.name);
  const superadmin = who.superadmin;
  const waitingAccount = !paused && extra.account === 'waiting';
  const next = waitingAccount
    ? {
        tip: 'Nobody has signed in to its Workspace account and accepted Google’s terms, so Gmail refuses what is done as it. Sign in to it once, as the Workspace steps say, and the Readiness check turns done.',
        instead: 'Until then its mail tools fail however much it holds; nothing else is lost by waiting.',
      }
    : paused
    ? {
        tip: superadmin ? 'Start it again from its page once the reason it was paused is settled; it gets back exactly the permissions it had.' : 'Ask a superadmin to start it again once the reason it was paused is settled; only they can.',
        instead: 'Leaving it paused keeps its history and everything it built untouched.',
      }
    : a.liveAssignments === 0
      ? {
          tip: superadmin ? 'Grant it an assignment from its page if it should work; capability comes from assignments and nothing else.' : 'Ask a superadmin to grant it an assignment if it should be working; capability comes from assignments and nothing else.',
          instead: 'Holding nothing is safe, so leaving it as it is costs nothing.',
        }
      : endingSoon.length > 0
        ? {
            tip: `${plural(endingSoon.length, 'assignment')} of it ${endingSoon.length === 1 ? 'is' : 'are'} running out. ${superadmin ? 'Grant a fresh one from its page if the work continues.' : 'Ask a superadmin to renew it if the work continues.'}`,
            instead: 'If you do nothing, what it granted stops at the end date and nothing has to be taken away.',
          }
        : {
            tip: a.agentKind === 'twin' ? `Nothing is needed. It prepares work for ${a.principal?.displayName ?? 'its person'} and never sends anything itself.` : 'Nothing is needed from you. It is working inside its assignments.',
            instead: superadmin ? 'If you ever need it to stop, pause it from its page; its history stays.' : 'If you need it to stop, ask a superadmin: only they can pause an agent.',
          };
  return {
    subject: { kind: a.agentKind === 'twin' ? 'twin' : 'agent', title: name },
    summary: `${name} is ${kind}. ${status}`,
    need: paused ? 'A superadmin decides whether it starts again.' : a.liveAssignments === 0 ? 'An assignment, from a superadmin, before it can do anything.' : 'Nothing: it is working within its assignments.',
    why: `${superadmin ? 'You see every agent as a superadmin.' : dept ? `You are in ${dept}, so you see its team.` : 'You may see it.'}`,
    source: { label: a.agentKind === 'twin' ? 'Made with its person’s place on the roster' : dept ? `Declared in ${dept}’s ledger file` : 'Declared in the ledger', when: dayWords(a.createdAt) },
    // The row records its sponsor, not who first wrote it.
    broughtBy: null,
    owner: sponsorParty,
    involved: a.principal ? [{ name: a.principal.displayName, kind: 'person', role: 'its person' }] : undefined,
    knows: others,
    facts: [
      ['Department', dept ?? 'None: it works through requests'],
      ['Holds', paused ? null : held || 'Nothing'],
      ['Live assignments', paused ? null : String(a.liveAssignments)],
      ['Its own address', a.workspaceEmail ? (extra.account ? ACCOUNT_WORDS[extra.account] : 'Yes, a Workspace account that someone must have signed in to once') : 'None; it prepares and never sends'],
      ['Paused by', paused ? [revoker?.name, dayWords(a.revokedAt)].filter(Boolean).join(', ') : null],
    ],
    next,
    open: { to: `/agents/${a.id}` },
  };
}

// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------

export function grantsWords(a: Pick<WireAssignment, 'scopes' | 'tools'>): string {
  return listWords([...a.scopes.map(scopeWords), ...a.tools.map(toolName)], 6) || 'nothing';
}

export function explainAssignment(a: WireAssignment, who: Who): ItemContext {
  const agent = asParty(who, a.agent.handle) ?? { name: `${a.agent.givenName} Agent`, kind: 'agent' as const };
  const agentName = agent.kind === 'twin' || agent.kind === 'agent' ? agent.name : `${a.agent.givenName} Agent`;
  const grants = grantsWords(a);
  const dept = a.agent.department ? who.label(a.agent.department) : null;
  const granter = asParty(who, a.grantedBy);
  const ender = a.endedBy ? asParty(who, a.endedBy) : null;
  const ends = a.validTo ? dayWords(a.validTo) : null;
  const superadmin = who.superadmin;
  const renew = superadmin ? 'grant a fresh one from the agent’s page' : 'ask a superadmin to grant a fresh one';
  const tip: Record<WireAssignment['state'], { tip: string; instead: string }> = {
    live: {
      tip: `Nothing is needed. It runs${ends ? ` until ${ends}` : ''} and what it grants goes with it on the next call after that.`,
      instead: superadmin ? 'If it should stop sooner, end it early from the list below; ending needs no code.' : 'If it should stop sooner, ask a superadmin: only they end an assignment.',
    },
    ending_soon: {
      tip: `It ends in ${plural(a.daysLeft ?? 0, 'day')}. If ${agentName} should keep this work, ${renew} before then.`,
      instead: 'If the work is done, do nothing: it lapses by itself and nobody has to take anything away.',
    },
    ended: {
      tip: 'Nothing to do. It is over, and what it granted is already gone.',
      instead: `If ${agentName} needs this again, ${renew}.`,
    },
    not_started: {
      tip: `Nothing to do yet. It starts on ${dayWords(a.validFrom)}.`,
      instead: superadmin ? 'If it was granted by mistake, end it from the list below before it starts.' : 'If it was granted by mistake, ask a superadmin to end it.',
    },
    no_end_date: {
      tip: superadmin
        ? 'Every assignment must have an end date and this one has none. End it from the list below, then grant a fresh one with an end date.'
        : 'Every assignment must have an end date and this one has none. Ask a superadmin to end it and grant one with an end date.',
      instead: 'Until it is fixed, what it grants never lapses by itself.',
    },
  };
  return {
    subject: { kind: 'assignment', title: a.title },
    summary: `${a.title}: ${agentName}${dept ? ` of ${dept}` : ''} may use ${grants}${ends ? `, until ${ends}` : ', with no end date'}.`,
    need: a.state === 'no_end_date' ? 'An end date; every assignment must have one.' : a.state === 'ending_soon' ? 'A decision on whether the work continues.' : undefined,
    why: `Capability follows the work: this is the only way ${agentName} comes to hold ${grants}. ${superadmin ? 'You see every assignment as a superadmin.' : dept ? `You see ${dept}’s.` : ''}`.trim(),
    source: { label: `A grant for “${a.title}”`, when: dayWords(a.validFrom) },
    broughtBy: granter ? { ...granter, role: 'granted it', when: dayWords(a.validFrom) } : null,
    owner: who.lead(a.agent.department),
    involved: [agent],
    knows: who.knowers(a.agent.department),
    facts: [
      ['Grants', grants],
      ['From', dayWords(a.validFrom)],
      ['Until', ends ?? 'No end date'],
      ['Ended early by', ender ? ender.name : null],
    ],
    next: tip[a.state],
    open: { to: `/agents/${a.agent.id}`, label: 'Open the agent' },
  };
}

// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------

const LEVEL_PLAIN: Record<Level, string> = {
  open_loop: 'a person approves every change',
  mixed: 'low-risk changes ship unattended',
  closed_loop: 'the verifier is trusted and the gate closes itself',
};

export function explainDepartment(d: WireDepartment, who: Who, detail?: WireDepartmentDetail | undefined): ItemContext {
  // The row itself says who leads it; that is the record, not the directory's copy.
  const leader = d.lead ? asParty(who, d.lead.email) : null;
  const lead: Party | null = d.lead ? { name: leader?.name === 'You' ? 'You' : d.lead.displayName, kind: 'person', role: `leads ${d.displayName}` } : null;
  const superadmin = who.superadmin;
  const stoppedBy = d.suspendedBy ? asParty(who, d.suspendedBy) : null;
  const raisable = detail?.dimensions.filter((x) => x.canRaise) ?? [];
  const change = d.autonomy.change as Level;
  const mail = d.autonomy.communication as Level;
  const next = d.suspendedAt
    ? {
        tip: superadmin ? 'Start its agents again from its page once the reason it was stopped is settled, under a code. Every other department keeps running.' : 'Ask a superadmin to start its agents again once the reason it was stopped is settled; only they can.',
        instead: 'Leaving it stopped changes nothing for the other departments.',
      }
    : !d.lead
      ? {
          tip: 'Name a lead on the roster. Until then requests to this department are refused, because nobody there could take them.',
          instead: 'Nothing else is lost by waiting, but nothing routed to it is answered.',
        }
      : raisable.length > 0
        ? {
            tip: `The evidence is there to move ${listWords(raisable.map((x) => x.dimension === 'change' ? 'changes' : x.dimension === 'communication' ? 'mail' : 'promotions'))} up a level. ${superadmin ? 'A superadmin still has to grant it, from the page.' : 'A superadmin still has to grant it; ask one if you want it.'}`,
            instead: 'Staying where it is costs nothing: a person keeps approving what the department asks for.',
          }
        : d.readOnly
          ? { tip: 'Nothing is needed. This department only reads; it prepares nothing that could change a record.', instead: 'If it should do more, that is a change to its ledger file.' }
          : {
              tip: 'Nothing is needed. Open its page to see the evidence for its level and what it still lacks to move up.',
              instead: superadmin ? 'If you want it to do less, you can lower its level or stop its agents from its page; lowering needs no evidence.' : 'If you want it to do less, ask a superadmin; lowering a level needs no evidence.',
            };
  return {
    subject: { kind: 'department', title: d.displayName },
    summary: `${d.displayName} is a department with ${d.agents.live} of ${d.agents.total} agents working. For changes ${LEVEL_PLAIN[change]}; for mail it is at ${LEVEL_SHORT[mail].toLowerCase()}.`,
    need: d.suspendedAt ? 'A superadmin decides when its agents start again.' : !d.lead ? 'A lead, named on the roster.' : raisable.length > 0 ? 'A superadmin’s decision on whether it moves up.' : undefined,
    why: `${superadmin ? 'You see every department as a superadmin.' : lead?.name === 'You' ? 'You lead it.' : 'You work in it.'}${d.suspendedAt ? ' Its agents are stopped.' : ''}`,
    source: { label: 'Declared in the ledger' },
    // The listing records who leads it and who stopped it, not who first made it.
    broughtBy: null,
    owner: lead,
    involved: who.agentsOf(d.name),
    knows: who.knowers(d.name),
    facts: [
      ['How it works', `Changes: ${LEVEL_LABEL[change]}`],
      ['Status', d.suspendedAt ? `Stopped ${dayWords(d.suspendedAt)}${stoppedBy ? ` by ${stoppedBy.name}` : ''}${d.suspensionReason ? `: “${clean(d.suspensionReason)}”` : ''}` : d.readOnly ? 'Reads only' : stateWords(d.status)],
    ],
    next,
    open: { to: `/departments/${d.name}` },
  };
}

// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------

const ORIGIN_WORDS = { core: 'a Google core tool', own: 'our own tool', third_party: 'a third party’s tool' } as const;

export interface ToolExtras {
  usage?: { count: number; resumesAt: string } | undefined;
  /** Where it is attached: which agents hold it (superadmin only). */
  where?: ReadonlyArray<{ department: string; agents: string[] }> | null | undefined;
  /** How the screen opens its own record. */
  openRecord?: (() => void) | undefined;
}

export function explainTool(t: WireTool, who: Who, extra: ToolExtras = {}): ItemContext {
  const name = toolName(t.id);
  const purpose = toolPurpose(t.id) || t.description || 'Its manifest has no description in the index yet.';
  const admitted = t.standing?.admitted === true;
  const withdrawn = t.standing !== null && !admitted;
  const thirdParty = t.economics?.origin === 'third_party';
  const ceiling = t.rateLimit?.requestsPerDay;
  const atCeiling = admitted && !!ceiling && !!extra.usage && extra.usage.count >= ceiling;
  const decider = t.standing ? asParty(who, t.standing.decidedBy) : null;
  const superadmin = who.superadmin;
  const holders = (t.departments ?? []).map(who.label);
  const agents = known((extra.where ?? []).flatMap((w) => w.agents.map((id) => who.actor(id))));
  const knowers = [...new Map((t.departments ?? []).flatMap((d) => who.knowers(d)).map((p) => [p.name, p] as const)).values()].slice(0, 4);
  const next = !t.manifestVersion
    ? { tip: 'It has no signed manifest in the ledger, so nothing can run it. A new tool is a reviewed change: ask Norum Tech from the chat.', instead: 'Nothing is lost by leaving it: it cannot be called.' }
    : !admitted
      ? {
          tip: superadmin
            ? `Admit it from its record if you want agents to be able to use it. ${
                t.transfer
                  ? t.transfer.marked
                    ? `${t.transfer.vendor} processes outside the EU, and its DPA and transfer mechanism are marked on the Workspace, so admitting is the only step left.`
                    : `${t.transfer.vendor} processes outside the EU, and its DPA and transfer mechanism are not marked on the Workspace yet: mark them first, or the admission is refused.`
                  : thirdParty
                    ? 'If its vendor processes outside the EU, the DPA and the transfer mechanism must be marked on the Workspace first.'
                    : 'It is signed, so admitting is the only step left.'
              }`
            : 'Ask a superadmin to admit it if you need it; only they can.',
          instead: withdrawn ? 'It was withdrawn, so leaving it as it is keeps it stopped everywhere.' : 'Leaving it unadmitted is safe: every call to it is refused.',
        }
      : atCeiling
        ? {
            tip: `It has used its day’s allowance (${ceiling} calls), so the gateway refuses it until ${extra.usage ? new Date(extra.usage.resumesAt).toLocaleString('en-GB') : 'tomorrow'}. Nothing is broken: wait, or ask for a different tool.`,
            instead: 'A ceiling is the platform’s pace for a person; it is not raised to get past it.',
          }
        : t.economics?.replaceDue
          ? {
              tip: 'Its monthly cost is over the threshold, so replacing it with our own is due. That is a build, so ask Norum Tech from the chat.',
              instead: 'Until it is replaced it keeps working and keeps costing.',
            }
          : {
              tip: 'Nothing is needed. Agents that hold it use it within its ceiling.',
              instead: superadmin ? 'If it should stop everywhere, withdraw it from its record; that needs no code.' : 'If it should stop, ask a superadmin to withdraw it.',
            };
  return {
    subject: { kind: 'tool', title: name },
    summary: `${name} is ${t.economics ? ORIGIN_WORDS[t.economics.origin] : 'a tool in the library'}. ${purpose}`,
    need: !t.manifestVersion ? 'A signed manifest.' : !admitted ? 'A superadmin’s admission before any agent can call it.' : atCeiling ? 'Waiting for the day to turn.' : undefined,
    why: 'The library lists every tool so that one that exists is granted, not built again.',
    source: t.manifestVersion ? { label: 'A signed manifest in the ledger' } : null,
    broughtBy: decider && t.standing ? { ...decider, role: admitted ? 'admitted it' : 'withdrew it', when: dayWords(t.standing.decidedAt) } : null,
    owner: undefined,
    involved: agents.length > 0 ? agents : undefined,
    knows: knowers.length > 0 ? knowers : undefined,
    facts: [
      ['Held by', holders.length > 0 ? listWords(holders) : 'No department yet'],
      ['Standing', admitted ? 'Admitted' : withdrawn ? 'Withdrawn' : 'Not yet admitted'],
      ['Risk', t.tier ? stateWords(t.tier) : null],
      ['Calls a day', ceiling ? String(ceiling) : null],
      ['Calls this month', t.economics ? String(t.economics.callsMonth) : null],
      // Only a vendor outside the EU has paperwork to mark (ADR-0128 §3, 0162).
      ['Outside the EU', t.transfer ? `${t.transfer.vendor}: its DPA and transfer mechanism are ${t.transfer.marked ? 'on record' : 'not on record yet'}` : null],
    ],
    next: { ...next, ...(extra.openRecord ? { actions: [{ label: 'Open its record', run: extra.openRecord, primary: true }] } : {}) },
  };
}

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

export function explainConnection(c: WireConnection, who: Who, now = Date.now()): ItemContext {
  const due = c.rotationDueAt !== null && Date.parse(c.rotationDueAt) <= now;
  const setEvent = [...c.history].filter((e) => e.kind === 'set' || e.kind === 'rotate').sort((a, b) => Date.parse(b.decidedAt) - Date.parse(a.decidedAt))[0];
  const setter = setEvent ? asParty(who, setEvent.decidedBy) : null;
  const holders = [...new Map(c.holders.map((h) => [h.handle, h])).values()];
  const tools = c.tools.map(toolName);
  const state = c.state;
  const next =
    state === 'unset'
      ? { tip: 'Put in a key from this record if a tool needs this service. A key is sent once to where keys are kept and never shown again, not even to you.', instead: 'Until then nothing can reach it, which is safe.' }
      : state === 'revoked'
        ? { tip: 'Every call to it is refused. Put in a new key from this record if the service should work again, and revoke the old one at the vendor’s own site too.', instead: 'Leaving it revoked keeps the service closed.' }
        : state === 'unknown'
          ? { tip: 'The platform cannot see where keys are kept from here, so it says nothing about this one. Check again once the store is reachable.', instead: 'Nothing is changed by looking again.' }
          : due
            ? { tip: 'The key is past the age at which it should be replaced. Rotate it from this record, then revoke the old one at the vendor’s own site.', instead: 'The old key keeps working until it is revoked, so waiting is a risk, not an outage.' }
            : { tip: `Nothing is needed. The key is set${c.rotationDueAt ? ` and is due to change on ${dayWords(c.rotationDueAt)}` : ''}.`, instead: 'If you ever suspect it leaked, revoke it from this record; that needs no code.' };
  return {
    subject: { kind: 'connection', title: c.vendor },
    summary: `${c.vendor} is an outside service the platform reaches only with a key, kept where keys are stored and never shown here. ${tools.length > 0 ? `It is reached by ${listWords(tools)}.` : 'No tool in the ledger uses it yet.'}`,
    need: state === 'unset' ? 'A key.' : state === 'revoked' ? 'A new key, if the service should work.' : due ? 'A new key to replace the old one.' : undefined,
    why: 'Keys are a superadmin’s to set, rotate and revoke.',
    source: c.whyNoKeylessPath ? { label: `A key is needed because ${c.whyNoKeylessPath.charAt(0).toLowerCase()}${c.whyNoKeylessPath.slice(1)}`.replace(/\.$/, '') } : null,
    broughtBy: setter && setEvent ? { ...setter, role: 'put the key in', when: dayWords(setEvent.decidedAt) } : null,
    owner: { ...who.me, role: 'holds the keys' },
    involved: holders.map((h) => ({ name: h.kind === 'twin' ? `${h.handle.charAt(0).toUpperCase()}${h.handle.slice(1)} · a twin` : `${h.handle.charAt(0).toUpperCase()}${h.handle.slice(1)} Agent`, kind: h.kind === 'twin' ? ('twin' as const) : ('agent' as const) })),
    knows: undefined,
    facts: [
      ['Key', state === 'set' ? `Set${c.setAt ? ` ${dayWords(c.setAt)}` : ''}` : state === 'unset' ? 'Never set' : state === 'revoked' ? 'Revoked' : 'Cannot be seen from here'],
      ['Changed every', `${plural(c.rotationTermDays, 'day')}`],
      ['Held by now', holders.length > 0 ? plural(holders.length, 'agent') : 'Nobody, so nothing can reach it today'],
    ],
    next: { ...next, actions: [{ label: 'What it may cost', to: '/plans' }] },
  };
}

// ---------------------------------------------------------------------------
// The audit log
// ---------------------------------------------------------------------------

/** "The approval code did not verify" → "the approval code did not verify", for the middle of a sentence. */
function lowerFirstWord(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

export function explainAuditRow(r: Pick<WireAuditRow, 'actorType' | 'actorId' | 'actorLabel' | 'onBehalfOfLabel' | 'action' | 'outcome' | 'tier' | 'at' | 'origin' | 'department' | 'target'> & { targetLabel?: string | null | undefined; rule?: string | null | undefined }, who: Who): ItemContext {
  // The rule a refusal was made under, from its own column or, before 0163, the gateway's suffix (ADR-0165).
  const { rule, action: bare } = ruleOfRow(r);
  const because = ruleWords(rule);
  const me = who.actor(r.actorId)?.name === 'You';
  const actor: Party = me
    ? { name: 'You', kind: 'person' }
    : { name: clean(r.actorLabel), kind: r.actorType === 'agent' ? (r.actorId.endsWith('.twin') ? 'twin' : 'agent') : r.actorType === 'system' ? 'system' : 'person' };
  const did = actionWords(bare).toLowerCase();
  const to = clean(targetWords({ target: r.target, targetLabel: r.targetLabel ?? null }));
  const out = outcomeWords(r);
  const refused = r.outcome === 'denied';
  const failed = r.outcome !== 'ok' && !refused;
  const startedBy: Record<string, string> = { chat: 'a person asked in the chat', confirmation: 'a person confirmed it', assignment: 'an assignment a person made', schedule: 'the platform’s schedule' };
  const started = r.origin ? (startedBy[r.origin] ?? humanise(r.origin).toLowerCase()) : null;
  return {
    subject: { kind: 'action', title: `${actor.name} ${did}` },
    summary: `${me ? 'You' : actor.name} ${did}${to !== '—' ? `: ${to}` : ''}. ${refused ? 'It was refused, so nothing was done.' : failed ? 'It failed, so nothing was changed by it.' : out.label.includes('CODE') ? 'It was done under a person’s approval code.' : 'It was done.'}`,
    why: `Every action is recorded so it can be traced to who did it${r.onBehalfOfLabel ? `, here on behalf of ${clean(r.onBehalfOfLabel)}` : ''}.`,
    source: started ? { label: `Started by ${started}`, when: dayWords(r.at) } : null,
    broughtBy: { ...actor, when: dayWords(r.at) },
    owner: undefined,
    ...(refused
      ? {
          facts: [
            ['Refused because', because ? because.title : rule ? 'A rule this screen has no words for yet' : 'Not recorded: it was refused before refusals named their rule'],
            ['Who can move it', because ? because.who : null],
          ] as ItemContext['facts'],
        }
      : {}),
    next: refused
      ? because
        ? {
            tip: `It was refused because ${lowerFirstWord(because.title)}. ${because.body} Nothing is needed unless it should have worked; if so, ${because.who.toLowerCase()} is the one who can move it.`,
            instead: 'A refusal is the platform working as intended; it is recorded so a pattern of them can be seen.',
            ...(who.superadmin && isCatalogued(rule) ? { actions: [{ label: 'Read the rule', to: `/refusals?code=${rule}` }] } : {}),
          }
        : {
            tip: 'Nothing is needed unless this was something that should have worked. If so, the Refusals page says in words what each rule means and who can move it.',
            instead: 'A refusal is the platform working as intended; it is recorded so a pattern of them can be seen.',
          }
      : failed
        ? { tip: 'The action failed and changed nothing. If it keeps failing, tell an engineer; a job that cannot do its work throws rather than report success.', instead: 'A single failure usually needs nothing: the platform tries again where it is safe to.' }
        : { tip: 'Nothing is needed. This line is the record that it happened and cannot be edited or removed.', instead: 'If something here looks wrong, ask the person or agent named above what they meant.' },
  };
}
